'use client'
import { MenuItem, MenuList, Link as AscendLink } from '@achieve/ascend'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'

function TermListClient({ list, alphabet }) {
  const refContainer = useRef()
  const [intersectingElements, setIntersectingElements] = useState(
    new Array(Object.keys(list ?? {}).length).fill(true)
  )
  const isUserClickRef = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isUserClickRef.current) {
          return
        }
        setIntersectingElements((prev) => {
          const updatedElements = [...prev]
          entries.forEach((entry) => {
            const sectionObservedIndex = entry.target.getAttribute('data-section-index')
            if (sectionObservedIndex !== null) {
              updatedElements[sectionObservedIndex] = entry.isIntersecting
            }
          })
          return updatedElements
        })
      },
      {
        rootMargin: '-150px',
        threshold: 0.01,
      }
    )

    const elements = document.querySelectorAll('[data-section-index]')
    elements.forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => {
      elements.forEach((ref) => {
        if (ref) observer.unobserve(ref)
      })
    }
  }, [list])

  useEffect(() => {
    setIntersectingElements(new Array(Object.keys(list ?? {}).length).fill(true))
  }, [list])

  const updateIntersectingElementsOnClick = (e) => {
    refContainer.current.focus()

    const linkElement = e.target.closest('[data-section-index]')
    if (!linkElement) return

    const sectionClicked = parseInt(linkElement.getAttribute('data-section-index'), 10)
    if (sectionClicked === -1 || isNaN(sectionClicked)) return

    isUserClickRef.current = true
    setIntersectingElements((prev) => {
      const newState = new Array(prev.length).fill(false)
      newState[sectionClicked] = true
      return newState
    })

    setTimeout(() => {
      isUserClickRef.current = false
    }, 1000)
  }

  const SubNavMenuItemCreator = () => {
    const minIntersectingElementIndex = intersectingElements.findIndex((el) => el)
    const existingLetters = Object.keys(list ?? {})

    return alphabet.map((letter, alphabetIndex) => {
      const sectionContentId = `${letter}`
      const sectionContentName = letter
      const disabled = !existingLetters.includes(letter)
      const actualSectionIndex = existingLetters.indexOf(letter)

      return (
        <MenuItem
          key={`menu-item-${letter}-${alphabetIndex}`}
          className={classNames(
            'whitespace-normal min-h-auto bg-neutral-0 p-0',
            '[&_a]:border-l-border-opaque [&_a]:text-neutral-300 [&_a]:no-underline [&_a]:w-full [&_a]:font-bold [&_a]:p-2 [&_a]:border-l-4 [&_a]:border-solid [&_a]:border-r-0 [&_a]:border-y-0',
            '[&_a_span]:ml-1 [&_a_span]:px-2 [&_a_span]:py-1 [&_a_span]:rounded-sm',
            '[&_a[aria-current=true]]:border-blue-500 [&_a[aria-current=true]]:bg-neutral-0 [&_a[aria-current=true]]:z-1 [&_a[aria-current=true]]:text-blue-500',
            '[&_a[aria-current=true]_span]:bg-blue-100',
            '[&_[data-disabled=true]_a]:text-neutral-150',
            // Styles for disabled items without content
            {
              'opacity-40 cursor-not-allowed': disabled,
            }
          )}
          tabIndex={-1}
          onClick={disabled ? undefined : updateIntersectingElementsOnClick}
          data-disabled={disabled}
        >
          <AscendLink
            data-section-index={actualSectionIndex}
            variant={'bodySm'}
            href={disabled ? null : `#${sectionContentId}`}
            disabled={disabled}
            aria-current={actualSectionIndex === minIntersectingElementIndex}
            className={disabled ? 'cursor-not-allowed pointer-events-none' : ''}
          >
            <span>{sectionContentName}</span>
          </AscendLink>
        </MenuItem>
      )
    })
  }

  return (
    <MenuList
      className={'sticky mb-8 p-0 top-24'}
      ref={refContainer}
      tabIndex={0}
      data-testid={'glossary-vertical-nav-menu-container'}
    >
      <SubNavMenuItemCreator />
    </MenuList>
  )
}

export default TermListClient
